import { useEffect, useMemo, useState } from "react";
import { X, Tag, Handshake, Search } from "lucide-react";
import { useStateValue } from "../../context/StateProvider";
import { buildTurnInSeed } from "../../utils/buildTurnInSeed";
import { listenBuyerLeads } from "../../utils/firebasefunctions";
import { normalizeStage } from "../../utils/buyerLeadStages";
import { clientNames, listClients } from "../../utils/leadClients";
import { resolveSystemSource } from "../../utils/leadSourceLabel";

// Deal stages that are NOT a sale yet — these are valid source requests
// to turn into a sale. Sale-stage deals (Under Contract, Pending, Closed)
// are filtered out.
const PRE_SALE_STAGES = [
  "Prospect",
  "On Hold",
  "CMAs",
  "Listing Appointment",
  "Pre-Market",
  "Coming Soon",
  "Active",
];

// Buyer pipeline stages close enough to a sale to turn in. The buyer side of
// this picker now sources from the Buyers Pipeline (these two columns), not
// from buyer file-request cards on the Deals Board.
const TURN_IN_BUYER_STAGES = ["Actively Showing", "Offer Submitted"];

// Roles that see everyone's requests (others see only their own).
const ALL_REQUEST_ROLES = ["Admin", "Supervisor", "Accountant"];

export default function SaleTurnInSourcePicker({ onSelect, onClose }) {
  const [{ user, deals, leadsource }] = useStateValue();
  const [side, setSide] = useState(null); // null | "listing" | "buyer"
  const [search, setSearch] = useState("");
  const [pickedId, setPickedId] = useState(null);

  // Buyer side sources from the Buyers Pipeline (not the Deals Board), so pull
  // the leads directly. Subscribe on mount — the picker is short-lived — and
  // track when they've loaded so the auto-skip below doesn't fire too early.
  const [buyerLeads, setBuyerLeads] = useState([]);
  const [buyerLeadsLoaded, setBuyerLeadsLoaded] = useState(false);
  useEffect(() => {
    const unsub = listenBuyerLeads((rows) => {
      setBuyerLeads(rows || []);
      setBuyerLeadsLoaded(true);
    });
    return () => unsub && unsub();
  }, []);

  const isBuyerSide = side === "buyer";

  // Source pool, role-scoped. Listing side: seller file-request deals in a
  // pre-sale stage. Buyer side: active buyer leads in the Actively Showing /
  // Offer Submitted columns — the buyers actually near a sale.
  const pool = useMemo(() => {
    if (!side) return [];
    const seesAll = ALL_REQUEST_ROLES.includes(user.role);
    if (isBuyerSide) {
      return (buyerLeads || []).filter(
        (l) =>
          (l.status || "active") === "active" &&
          TURN_IN_BUYER_STAGES.includes(normalizeStage(l.stage)) &&
          (seesAll || l.assignedAgent === user.id),
      );
    }
    // Any seller listing on the board in a pre-sale stage can be turned in —
    // not just file-request ones — so picking it MOVES that card to Under
    // Contract. (Sale-stage deals are already excluded by PRE_SALE_STAGES.)
    return (deals || []).filter(
      (d) =>
        d.dealtype === "Seller" &&
        PRE_SALE_STAGES.includes(d.dealStage) &&
        (seesAll || d.agentData === user.id),
    );
  }, [side, isBuyerSide, deals, buyerLeads, user]);

  // Per-item accessors so the same list UI renders deals (listing) or leads
  // (buyer) without branching the JSX.
  const itemKey = (it) => (isBuyerSide ? it.id : it.createdat);
  const itemTitle = (it) =>
    isBuyerSide
      ? clientNames(it) || it.contactName || "(unnamed buyer)"
      : it.propertyaddress || "(no address)";
  const itemSubtitle = (it) =>
    isBuyerSide
      ? normalizeStage(it.stage) || "—"
      : `${it.clientLastName || "—"} • ${it.dealStage || "—"}`;

  // Auto-skip phase 2 if the pool is empty after side is chosen — carry the
  // side so the deal still opens pre-set to Buyer/Seller. For the buyer side,
  // wait for the leads to load so we don't skip before they arrive.
  useEffect(() => {
    if (side === null) return;
    if (isBuyerSide && !buyerLeadsLoaded) return;
    if (pool.length === 0) onSelect(buildTurnInSeed({}, side));
  }, [side, isBuyerSide, buyerLeadsLoaded, pool.length, onSelect]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return pool;
    return pool.filter((it) =>
      `${itemTitle(it)} ${itemSubtitle(it)}`.toLowerCase().includes(q),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pool, search, isBuyerSide]);

  const handleContinue = () => {
    const picked = pool.find((it) => itemKey(it) === pickedId);
    if (!picked) return;
    // Buyer leads carry different field names than deals, so map a lead into
    // the seed's source shape (mirrors BuyerLeadDrawer's "Offer Accepted").
    const source = isBuyerSide
      ? {
          dealtype: "Buyer",
          customers: listClients(picked).map((c) => c.contactId),
          agentName: picked.assignedAgentName || "",
          agentData: picked.assignedAgent || "",
          leadsource:
            resolveSystemSource(picked.leadSource, leadsource) ||
            picked.leadSource ||
            "",
          // Archive this pipeline lead as a won buyer once the deal is created.
          sourceLeadId: picked.id,
          sourceLeadType: "buyer",
        }
      : picked;
    onSelect(buildTurnInSeed(source, side));
  };

  // "Start From Scratch" still respects the side the agent picked, so the
  // client type comes in pre-selected.
  const handleScratch = () => onSelect(buildTurnInSeed({}, side));

  // Side picker phase
  if (side === null) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
          <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
            <div className="text-base font-semibold text-gray-900">Sale Turn In</div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-700" aria-label="Close">
              <X size={18} />
            </button>
          </div>
          <div className="p-6">
            <div className="text-sm text-gray-700 font-medium mb-4 text-center">
              Where Is This Sale Coming From?
            </div>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setSide("listing")}
                className="border-2 border-gray-200 hover:border-blue-500 hover:bg-blue-50 rounded-lg p-4 flex flex-col items-center gap-2 transition"
              >
                <Tag size={22} className="text-blue-600" />
                <div className="text-sm font-semibold text-gray-900">Listing Side</div>
                <div className="text-xs text-gray-500 text-center">We Listed It</div>
              </button>
              <button
                type="button"
                onClick={() => setSide("buyer")}
                className="border-2 border-gray-200 hover:border-blue-500 hover:bg-blue-50 rounded-lg p-4 flex flex-col items-center gap-2 transition"
              >
                <Handshake size={22} className="text-blue-600" />
                <div className="text-sm font-semibold text-gray-900">Buyer Side</div>
                <div className="text-xs text-gray-500 text-center">We Represent the Buyer</div>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Phase 2: request picker (also covers the brief render before auto-skip)
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="text-base font-semibold text-gray-900">
            Pick {side === "listing" ? "a Listing" : "a Buyer"}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700" aria-label="Close">
            <X size={18} />
          </button>
        </div>
        <div className="p-4 border-b border-gray-100">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by address or client…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded text-sm"
            />
          </div>
        </div>
        <div className="overflow-y-auto flex-1 p-3 space-y-2">
          {filtered.length === 0 ? (
            <div className="text-sm text-gray-500 text-center py-6">
              {isBuyerSide ? "No matching buyers." : "No matching requests."}
            </div>
          ) : (
            filtered.map((it) => (
              <button
                key={itemKey(it)}
                type="button"
                onClick={() => setPickedId(itemKey(it))}
                className={`w-full text-left rounded-lg p-3 border-2 transition ${
                  pickedId === itemKey(it)
                    ? "border-blue-500 bg-blue-50"
                    : "border-gray-200 bg-white hover:border-gray-300"
                }`}
              >
                <div className="font-semibold text-gray-900 text-sm">
                  {itemTitle(it)}
                </div>
                <div className="text-xs text-gray-500 mt-0.5">
                  {itemSubtitle(it)}
                </div>
              </button>
            ))
          )}
        </div>
        <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={handleScratch}
            className="text-sm text-gray-600 hover:text-gray-900 underline"
          >
            Start From Scratch
          </button>
          <button
            type="button"
            onClick={handleContinue}
            disabled={!pickedId}
            className="px-4 py-2 bg-blue-600 text-white rounded text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
          >
            Continue →
          </button>
        </div>
      </div>
    </div>
  );
}
